import React, { useState } from 'react'
import type { QuizAttemptSummary } from '@/types/quiz'

interface ResetQuizAttemptsModalProps {
  quizId: string
  summaries: QuizAttemptSummary[]
  isLoading: boolean
  onConfirmReset: (enrollmentId: string) => void
  onClose: () => void
}

export const ResetQuizAttemptsModal: React.FC<ResetQuizAttemptsModalProps> = ({
  summaries,
  isLoading,
  onConfirmReset,
  onClose,
}) => {
  const [selectedEnrollmentId, setSelectedEnrollmentId] = useState<string | null>(null)

  const handleConfirm = () => {
    if (selectedEnrollmentId) {
      onConfirmReset(selectedEnrollmentId)
      onClose()
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Reset Quiz Attempts</h3>
          <button
            onClick={onClose}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading ? (
            <div className="text-center py-8 text-gray-500">Loading students...</div>
          ) : summaries.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No students have attempted this quiz yet.
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600 mb-4">
                Select a student to reset their quiz attempts. This will delete all their attempts
                and allow them to retake the quiz.
              </p>
              <div className="space-y-2">
                {summaries.map((summary) => (
                  <button
                    key={summary.enrollmentId}
                    onClick={() => setSelectedEnrollmentId(summary.enrollmentId)}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                      selectedEnrollmentId === summary.enrollmentId
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{summary.userName}</div>
                        <div className="text-sm text-gray-500">{summary.userEmail}</div>
                        <div className="flex items-center gap-4 mt-2 text-sm">
                          <span className="text-gray-600">
                            Attempts: <span className="font-medium">{summary.attemptsCount}</span>
                          </span>
                          <span className="text-gray-600">
                            Best Score:{' '}
                            <span className="font-medium text-green-600">
                              {summary.bestScore}%
                            </span>
                          </span>
                          {summary.latestScore !== undefined && (
                            <span className="text-gray-600">
                              Latest:{' '}
                              <span className="font-medium text-blue-600">
                                {summary.latestScore}%
                              </span>
                            </span>
                          )}
                        </div>
                        {summary.lastAttemptAt && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Last attempt: {new Date(summary.lastAttemptAt).toLocaleString()}
                          </div>
                        )}
                      </div>
                      {selectedEnrollmentId === summary.enrollmentId && (
                        <svg
                          className="w-6 h-6 text-blue-500 flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedEnrollmentId}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            Reset Attempts
          </button>
        </div>
      </div>
    </div>
  )
}
